// src/pages/PaymentAccounting/PaymentAccountingTable.tsx
import React from 'react';
import { Badge, Button } from 'reactstrap';
import { useLocation } from 'react-router-dom';
import { PageActionPermission } from 'services/pageActionPermission/PageActionPermission';
import { PrizeClaimDossier, CLAIM_DOSSIER_PAYMENT_STATUS_LABELS, CLAIM_DOSSIER_PAYMENT_STATUS_BADGE } from 'pages/ClaimDossier/types';
import DataTableShell from 'Components/Common/DataTableShell';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('payment_accounting');

interface Props {
  dossiers: PrizeClaimDossier[];
  loading: boolean;
  payingId?: number | null;
  onPay: (dossier: PrizeClaimDossier) => void;
}

const formatCurrency = (v: number) => st('value_d_text', { value: Number(v || 0).toLocaleString('vi-VN') });

const PAYMENT_ACTION_COLUMN_CODES = ['button-pay'];

const PaymentAccountingTable: React.FC<Props> = ({ dossiers, loading, payingId, onPay }) => {
  const location = useLocation();
  const canShowActionColumn = PageActionPermission.canDisplayAny(
    location.pathname,
    PAYMENT_ACTION_COLUMN_CODES,
  );
  const columnCount = canShowActionColumn ? 8 : 7;

  return (
  <DataTableShell
    headerRow={
      <tr>
        <th className="text-center" style={{ width: 60 }}>{st('stt_column')}</th>
        <th>{st('ma_ho_so_column')}</th>
        <th>{st('nguoi_nhan_column')}</th>
        <th>{st('tai_khoan_ngan_hang_column')}</th>
        <th>{st('so_tien_chi_tra_column')}</th>
        <th>{st('trang_thai_chi_column')}</th>
        <th>{st('kt_thuc_hien_column')}</th>
        {canShowActionColumn && <th>{st('thao_tac_column')}</th>}
      </tr>
    }
  >
        {loading ? (
          <tr>
            <td colSpan={columnCount} className="text-center py-4">
              <span className="spinner-border spinner-border-sm" />
            </td>
          </tr>
        ) : dossiers.length === 0 ? (
          <tr>
            <td colSpan={columnCount} className="text-center text-muted py-4">
              {st('chua_co_ho_so_nao_can_text')}
            </td>
          </tr>
        ) : (
          dossiers.map((d, rowIndex) => (
            <tr key={d.id}>
              <td className="text-center">{rowIndex + 1}</td>
              <td>
                <b>{d.dossierCode}</b>
                <br />
                <span className="text-muted fs-12">
                  {st('ve_text')} {d.symbol}-{d.ticketNumber}
                </span>
              </td>
              <td>
                {d.claimantName}
                <br />
                <span className="text-muted fs-12">{st('cccd_text')} {d.claimantIdNumber}</span>
              </td>
              <td>
                {d.bankName}
                <br />
                <span className="text-muted fs-12">
                  {d.bankAccountName} — {d.bankAccountNumber}
                </span>
              </td>
              <td className="fw-bold">{formatCurrency(d.netAmount)}</td>
              <td>
                <Badge color={CLAIM_DOSSIER_PAYMENT_STATUS_BADGE[d.paymentStatus]}>
                  {CLAIM_DOSSIER_PAYMENT_STATUS_LABELS[d.paymentStatus]}
                </Badge>
              </td>
              <td>
                {d.paymentStatus === 'DaChi' ? (
                  <>
                    {d.paymentByName || (d.paymentBy ? `#${d.paymentBy}` : '')}
                    {d.paymentAt && (
                      <>
                        <br />
                        <span className="text-muted fs-12">{new Date(d.paymentAt).toLocaleString('vi-VN')}</span>
                      </>
                    )}
                  </>
                ) : (
                  <span className="text-muted">—</span>
                )}
              </td>
              {canShowActionColumn && (
                <td>
                  {d.paymentStatus === 'ChuaChi' ? (
                    PageActionPermission.canDisplay(location.pathname, 'button-pay') && (
                      <Button
                        color="primary"
                        size="sm"
                        data-action-code="button-pay"
                        data-action-name="Chuyển khoản"
                        onClick={() => onPay(d)}
                        disabled={payingId === d.id}
                      >
                        {payingId === d.id ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-1" />
                            {st('dang_chuyen_text')}
                          </>
                        ) : (
                          <>
                            <i className="ri-bank-card-line me-1"></i>{st('chuyen_khoan_text')}
                          </>
                        )}
                      </Button>
                    )
                  ) : (
                    <span className="text-success fs-12">
                      <i className="ri-checkbox-circle-fill align-bottom me-1"></i>{st('da_chi_tra_text')}
                    </span>
                  )}
                </td>
              )}
            </tr>
          ))
        )}
  </DataTableShell>
  );
};

export default PaymentAccountingTable;
